import type { FragmentType } from "../../../../__generated__/gql";
import { graphql, useFragment } from "../../../../__generated__/gql";
import { ErrorIcon } from "../../../_components/dx-icons";

import { DxDivider } from "./dx-divider";
import { DxInformationItem } from "./dx-information-item";
import { DxSectionHeader } from "./dx-section-header";

export const DxFaqSectionFragment = graphql(`
  fragment DxFaqSection_Site on Site {
    content {
      faqs {
        question
        answer
      }
    }
  }
`);

type Faq = { question: string; answer: string };

const LG_COLUMN_COUNT = 3;

/** items を N 列に row-major で分配 ([A,B,C,D] → [[A,D],[B],[C]] for N=3)。
 * CSS Grid masonry が Chrome 未対応のため、 各列を独立した flex stack にして
 * D を A の直下に詰める Pinterest 風レイアウトを CSS だけで保証する。 */
function splitRowMajor<T>(items: T[], columnCount: number): T[][] {
  const columns: T[][] = Array.from({ length: columnCount }, () => []);
  items.forEach((item, i) => {
    const col = columns[i % columnCount];
    if (col) col.push(item);
  });
  return columns;
}

function FaqItem({ faq }: { faq: Faq }) {
  return (
    // Information の各項目には注意喚起の「!」アイコン (error_outline) を付ける
    // (DXPJ-62 htJLH / 2026-06-29 定例。Figma 1020-10593)。
    <DxInformationItem title={faq.question} icon={<ErrorIcon />}>
      <div
        className="dx-body-2 text-dx-text-default [&_a]:underline"
        dangerouslySetInnerHTML={{ __html: faq.answer }}
      />
    </DxInformationItem>
  );
}

/** 拠点 FAQ。 answer は CMS で編集する信頼済み HTML。 */
export function DxFaqSection({
  site: siteData,
}: {
  site: FragmentType<typeof DxFaqSectionFragment>;
}) {
  const site = useFragment(DxFaqSectionFragment, siteData);
  const faqs = site.content.faqs;
  if (faqs.length === 0) return null;
  const lgColumns = splitRowMajor(faqs, LG_COLUMN_COUNT);

  return (
    <>
      <DxDivider />
      <section className="py-[16px]">
        <DxSectionHeader title="Information" />
        {/* SP: 単純縦並び。 */}
        <ul className="flex flex-col gap-y-[24px] px-[16px] pt-[8px] lg:hidden">
          {faqs.map((faq, i) => (
            <li key={i}>
              <FaqItem faq={faq} />
            </li>
          ))}
        </ul>
        {/* lg+: 3 列を独立 flex stack で出し、 D を A 直下に詰める (Chrome は
         * grid-template-rows: masonry 未対応のため JS で列分配)。 */}
        <div className="hidden gap-x-[24px] px-[16px] pt-[8px] lg:grid lg:grid-cols-3 lg:items-start">
          {lgColumns.map((column, columnIndex) => (
            <ul key={columnIndex} className="flex flex-col gap-y-[24px]">
              {column.map((faq, i) => (
                <li key={i}>
                  <FaqItem faq={faq} />
                </li>
              ))}
            </ul>
          ))}
        </div>
      </section>
    </>
  );
}
